import { useEffect, useState } from 'react';
import { CancellationSurveyModal } from './CancellationSurveyModal';
import { OfferModal } from './OfferModal';
import { OfferAcceptedModal } from './OfferAcceptedModal';
import { GoodbyeModal } from './GoodbyeModal';
import { useCancellationFlow } from '../../hooks/useCancellationFlow';
import { CANCELLATION_STEPS } from '../../constants/cancellationFlow';

interface CancellationFlowOrchestratorProps {
    isOpen: boolean;
    platformSubscriptionId: string | null;
    hasTakenFreeMonth?: boolean;
    shouldShowOffer: boolean;
    onComplete: () => void;
}

export const CancellationFlowOrchestrator = ({
    isOpen,
    platformSubscriptionId,
    hasTakenFreeMonth = false,
    shouldShowOffer,
    onComplete,
}: CancellationFlowOrchestratorProps) => {
    // Create a key that changes when flow reopens to force complete remount
    const [flowKey, setFlowKey] = useState(0);

    useEffect(() => {
        if (isOpen) {
            // Increment key to force complete remount and reset all state
            setFlowKey(prev => prev + 1);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <CancellationFlowContent
            key={flowKey}
            platformSubscriptionId={platformSubscriptionId}
            hasTakenFreeMonth={hasTakenFreeMonth}
            shouldShowOffer={shouldShowOffer}
            onComplete={onComplete}
        />
    );
};

// Separate component so the hook resets when key changes
const CancellationFlowContent = ({
    platformSubscriptionId,
    hasTakenFreeMonth,
    shouldShowOffer,
    onComplete,
}: Omit<CancellationFlowOrchestratorProps, 'isOpen'>) => {
    const {
        currentStep,
        offerType,
        handleSurveyComplete,
        handleOfferAccept,
        handleOfferDecline,
        handleOfferAcceptedClose,
        handleGoodbyeEmojiClick,
        handleGoodbyeClose,
    } = useCancellationFlow({
        platformSubscriptionId,
        hasTakenFreeMonth,
        shouldShowOffer,
        onComplete,
    });

    return (
        <>
            {/* Step 1: Survey */}
            <CancellationSurveyModal
                isOpen={currentStep === CANCELLATION_STEPS.SURVEY}
                onClose={onComplete}
                onComplete={handleSurveyComplete}
                hasTakenFreeMonth={hasTakenFreeMonth}
            />

            {/* Step 2: Offer */}
            <OfferModal
                isOpen={currentStep === CANCELLATION_STEPS.OFFER}
                offerType={offerType}
                onAccept={handleOfferAccept}
                onDecline={handleOfferDecline}
            />

            {/* Step 3: Offer Accepted Confirmation */}
            <OfferAcceptedModal
                isOpen={currentStep === CANCELLATION_STEPS.OFFER_ACCEPTED}
                offerType={offerType}
                onClose={handleOfferAcceptedClose}
            />

            {/* Step 4: Goodbye (with emoji survey) */}
            <GoodbyeModal
                isOpen={currentStep === CANCELLATION_STEPS.GOODBYE}
                onClose={handleGoodbyeClose}
                onEmojiClick={handleGoodbyeEmojiClick}
            />
        </>
    );
};
